'use client';
/**
 * Le formulaire d'une CONVENTION ou d'un ÉVÉNEMENT rattaché à un contact
 * (RQ, 12/10/2026 : « la gestion des conventions, je voudrais que ce soit dans
 * les contacts. Quand je clique sur un contact je peux lui ajouter une
 * convention, un événement »). Sorti de l'écran Calendrier, qui ne fait plus
 * que les AFFICHER. Les dates se calculent au moteur : l'aperçu passe par
 * `POST …/apercu`, jamais par une copie du calcul.
 */
import React from 'react';
import { useGed } from '@/lib/store';
import { fetchEcriture } from '@/lib/ecrire';
import { couleurDe, COULEURS_CONVENTION, type ConventionVue, type RegleConv } from '@/lib/conventions';
import { inputStyle, Modale, Selecteur } from '@/components/ui';
import { Bouton } from '@/shared/Bouton';
import { Segment } from '@/shared/Segment';
import { ChoixCorrespondant, contactsParIds } from '@/components/ChoixCorrespondant';

const NOMS_JOURS = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
const RANGS: [string, string][] = [['1', 'premier'], ['2', 'deuxième'], ['3', 'troisième'], ['4', 'quatrième'], ['5', 'cinquième'], ['-1', 'dernier']];
const lbl: React.CSSProperties = { display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: 4 };

type Nature = 'convention' | 'evenement';
type Type = RegleConv['type'];

export function FormulaireConvention({ valeur, contact, natureInitiale = 'convention', onFerme, onFait }: {
  valeur: ConventionVue | null;
  /** Le contact dont on ouvre la fiche : fixé, il n'est plus à choisir. */
  contact?: { code: string; nom: string };
  natureInitiale?: Nature;
  onFerme: () => void; onFait: () => void;
}) {
  const { showToast } = useGed();
  const r = valeur?.regle;
  const [nature, setNature] = React.useState<Nature>(valeur?.nature || natureInitiale);
  const estEvt = nature === 'evenement';
  const [nom, setNom] = React.useState(valeur?.nom || (contact && natureInitiale === 'convention' ? contact.nom : ''));
  /* Le contact de l'ANNUAIRE (L3, 10/10/2026 ; le carnet est supprimé le 20/10/2026) : choisi, le nom se remplit s'il est vide. */
  const [contactCode, setContactCode] = React.useState(contact?.code || valeur?.contactCode || '');
  /* Un événement est d'abord une date unique ; il peut se répéter « selon le
     besoin » (RQ, 12/10/2026) — les mêmes rythmes qu'une convention. */
  const [type, setType] = React.useState<Type>(r?.type || (natureInitiale === 'evenement' ? 'ponctuel' : 'rang_mensuel'));
  const [dateUnique, setDateUnique] = React.useState(r?.type === 'ponctuel' ? r.date : '');
  const [rang, setRang] = React.useState(String(r?.type === 'rang_mensuel' ? r.rang : 1));
  const [jour, setJour] = React.useState(String(r?.type === 'rang_mensuel' ? r.jourSemaine : 1));
  const [depart, setDepart] = React.useState(r && (r.type === 'intervalle' || r.type === 'cycle') ? r.depart : '');
  const [semaines, setSemaines] = React.useState(String(r?.type === 'intervalle' ? r.semaines : 4));
  const [actives, setActives] = React.useState(String(r?.type === 'cycle' ? r.semainesActives : 2));
  const [arret, setArret] = React.useState(String(r?.type === 'cycle' ? r.semainesArret : 2));
  const [couleur, setCouleur] = React.useState(valeur?.couleur || 'cyan');
  const [du, setDu] = React.useState(valeur?.du || '');
  const [au, setAu] = React.useState(valeur?.au || '');
  const [exceptions, setExceptions] = React.useState<string[]>(valeur?.exceptions || []);
  const [nouvelleExc, setNouvelleExc] = React.useState('');
  const [note, setNote] = React.useState(valeur?.note || '');
  const [actif, setActif] = React.useState(valeur?.actif !== false);
  const [apercu, setApercu] = React.useState<{ description: string; prochaines: { date: string; semaine: string | null }[] } | string | null>(null);
  const [envoi, setEnvoi] = React.useState(false);

  const regle = (): Record<string, unknown> => (type === 'ponctuel' ? { type, date: dateUnique } : type === 'rang_mensuel'
    ? { type, rang: Number(rang), jourSemaine: Number(jour) }
    : type === 'intervalle' ? { type, depart, semaines: Number(semaines) }
      : { type, depart, semainesActives: Number(actives), semainesArret: Number(arret) });
  /* L'ÉTAT INITIAL DU FORMULAIRE, figé au premier rendu (14/10/2026) : envoyé
     en `base` à la modification, il permet au serveur de n'écrire que ce que
     l'utilisateur a changé — et de refuser si un autre a changé le même champ
     pendant la saisie (fusion à trois voies). */
  const [baseLue] = React.useState(() => ({ nom, nature, contactCode, regle: regle(), couleur, du, au, exceptions, note, actif }));

  // L'aperçu se recalcule à chaque changement : on VOIT les dates avant d'enregistrer.
  const cle = JSON.stringify([regle(), du, au, exceptions]);
  React.useEffect(() => {
    let vivant = true;
    const t = setTimeout(() => {
      void fetchEcriture('/api/ged/ged/rh/conventions/apercu', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ regle: JSON.parse(cle)[0], du: du || null, au: au || null, exceptions }),
      }).then(async (rep) => {
        const d = await rep.json().catch(() => ({}));
        if (vivant) setApercu(rep.ok ? d : (d?.error?.message || 'Règle incomplète.'));
      });
    }, 250);
    return () => { vivant = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cle]);

  const enregistrer = async () => {
    setEnvoi(true);
    try {
      /* ⚠️ LA DATE EN COURS DE SAISIE PART AVEC LES AUTRES (09/10/2026). On tape
         une date d'exception, on clique « Enregistrer » sans passer par
         « Ajouter » : elle disparaissait sans un mot, et l'aperçu n'en tenait
         pas compte non plus. Même défaut que les mots-clés de la correction de
         métadonnées, corrigé là de la même façon. */
      const exceptionsFinales = nouvelleExc && !exceptions.includes(nouvelleExc)
        ? [...exceptions, nouvelleExc].sort()
        : exceptions;
      const rep = await fetchEcriture('/api/ged/ged/rh/conventions', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: valeur?.id, nom, nature, contactCode, regle: regle(), couleur, du, au, exceptions: exceptionsFinales, note, actif, base: valeur ? baseLue : undefined }),
      });
      const d = await rep.json().catch(() => ({}));
      if (!rep.ok) { showToast(d?.error?.message || 'Enregistrement refusé.', 'erreur'); return; }
      showToast(estEvt ? 'Événement enregistré.' : 'Convention enregistrée.');
      onFait();
    } finally { setEnvoi(false); }
  };

  const jourCourt = (d: string) => `${NOMS_JOURS[(new Date(`${d}T12:00:00`).getDay() + 6) % 7]!.slice(0, 3)}. ${d.slice(8)}/${d.slice(5, 7)}`;

  return (
    <Modale titre={`${valeur ? (estEvt ? 'Modifier l’événement' : 'Modifier la convention') : (estEvt ? 'Nouvel événement' : 'Nouvelle convention')}${contact ? ` — ${contact.nom}` : ''}`} largeur={620} onClose={onFerme}
      pied={<>
        <Bouton taille="sm" type="button" onClick={onFerme}>Annuler</Bouton>
        <Bouton variante="primaire" type="button" disabled={envoi || nom.trim().length < 2} onClick={() => void enregistrer()}>
          {envoi ? 'Enregistrement…' : 'Enregistrer'}
        </Bouton>
      </>}>
      <div style={{ display: 'grid', gap: 12 }}>
        <Segment ariaLabel="Nature" valeur={nature} onChange={(v) => { setNature(v); if (!valeur) setType(v === 'evenement' ? 'ponctuel' : 'rang_mensuel'); }}
          options={[
            { valeur: 'convention', libelle: 'Convention', aide: 'Un accord avec le contact, qui se répète (passages, prélèvements, prise en charge…)' },
            { valeur: 'evenement', libelle: 'Événement', aide: 'Un rendez-vous, une visite, une livraison… à une date, ou répété selon le besoin' },
          ]} />
        {!contact && <div><label style={lbl} htmlFor="cv-contact">Contact de l’annuaire</label>
          <ChoixCorrespondant id="cv-contact" valeur={contactCode}  vide="— à rattacher —"
            onChange={(code) => { setContactCode(code); if (code && !nom.trim()) void contactsParIds([code]).then((m) => setNom((n) => n.trim() ? n : m.get(code)?.nom || '')); }}
            aide="Relie la convention à la fiche du contact dans l’annuaire." /></div>}
        <div><label style={lbl} htmlFor="cv-nom">{estEvt ? 'Intitulé de l’événement' : 'Nom affiché au calendrier'}</label>
          <input id="cv-nom" value={nom} onChange={(e) => setNom(e.target.value)} style={inputStyle} placeholder={estEvt ? 'Ex. Visite de la CNAM, livraison annuelle…' : 'Ex. Clinique de Carthage'} /></div>

        <div><label style={lbl} htmlFor="cv-type">Rythme</label>
          <Selecteur id="cv-type" value={type} onChange={(v) => setType(v as Type)} style={inputStyle}>
            <option value="ponctuel">Une seule date</option>
            <option value="rang_mensuel">Un jour précis du mois (ex. le premier lundi)</option>
            <option value="intervalle">Un jour toutes les N semaines (ex. toutes les 4 semaines)</option>
            <option value="cycle">Des semaines d’activité, puis des semaines d’arrêt</option>
          </Selecteur></div>

        {type === 'ponctuel' && (
          <div style={{ maxWidth: 220 }}><label style={lbl} htmlFor="cv-date">Date</label>
            <input id="cv-date" type="date" value={dateUnique} onChange={(e) => setDateUnique(e.target.value)} style={inputStyle} /></div>
        )}
        {type === 'rang_mensuel' && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 160px' }}><label style={lbl} htmlFor="cv-rang">Le</label>
              <Selecteur id="cv-rang" value={rang} onChange={setRang} style={inputStyle}>{RANGS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</Selecteur></div>
            <div style={{ flex: '1 1 160px' }}><label style={lbl} htmlFor="cv-jour">Jour</label>
              <Selecteur id="cv-jour" value={jour} onChange={setJour} style={inputStyle}>{NOMS_JOURS.map((l, i) => <option key={l} value={i + 1}>{l}</option>)}</Selecteur></div>
            <span style={{ alignSelf: 'end', fontSize: 13, color: 'var(--text-muted)', paddingBottom: 9 }}>de chaque mois</span>
          </div>
        )}
        {(type === 'intervalle' || type === 'cycle') && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 170px' }}><label style={lbl} htmlFor="cv-depart">À partir du (date de départ)</label>
              <input id="cv-depart" type="date" value={depart} onChange={(e) => setDepart(e.target.value)} style={inputStyle} /></div>
            {type === 'intervalle' ? (
              <div style={{ flex: '1 1 120px' }}><label style={lbl} htmlFor="cv-sem">Toutes les (semaines)</label>
                <input id="cv-sem" type="number" min={1} max={52} value={semaines} onChange={(e) => setSemaines(e.target.value)} style={inputStyle} /></div>
            ) : (
              <>
                <div style={{ flex: '1 1 120px' }}><label style={lbl} htmlFor="cv-act">Semaines d’activité</label>
                  <input id="cv-act" type="number" min={1} max={52} value={actives} onChange={(e) => setActives(e.target.value)} style={inputStyle} /></div>
                <div style={{ flex: '1 1 120px' }}><label style={lbl} htmlFor="cv-arr">Semaines d’arrêt</label>
                  <input id="cv-arr" type="number" min={0} max={52} value={arret} onChange={(e) => setArret(e.target.value)} style={inputStyle} /></div>
              </>
            )}
          </div>
        )}

        <div style={{ background: 'var(--surface-sunken)', borderRadius: 'var(--radius-sm)', padding: '10px 12px', fontSize: 13 }} aria-live="polite">
          {typeof apercu === 'string' ? <span style={{ color: 'var(--amber-700)' }}>{apercu}</span>
            : apercu ? (
              <>
                <strong>{apercu.description}</strong>
                <div style={{ color: 'var(--text-muted)', marginTop: 3 }}>
                  Prochaines dates : {apercu.prochaines.length ? [...new Map(apercu.prochaines.map((p) => [p.date, p])).values()].slice(0, 8).map((p) => jourCourt(p.date)).join(' · ') : 'aucune sur les 200 prochains jours'}
                </div>
              </>
            ) : 'Calcul…'}
        </div>

        <div>
          <span style={lbl}>Couleur</span>
          <div style={{ display: 'flex', gap: 8 }}>
            {COULEURS_CONVENTION.map((c) => {
              const col = couleurDe(c);
              return <button key={c} type="button" aria-label={c} aria-pressed={couleur === c} onClick={() => setCouleur(c)}
                style={{ width: 26, height: 26, borderRadius: '50%', cursor: 'pointer', background: col.fond, border: `3px solid ${couleur === c ? col.texte : col.bord}` }} />;
            })}
          </div>
        </div>

        <details open={!!(du || au || exceptions.length || note)}>
          <summary style={{ cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: 'var(--text-muted)' }}>Validité, exceptions, note</summary>
          <div style={{ display: 'grid', gap: 10, marginTop: 8 }}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 150px' }}><label style={lbl} htmlFor="cv-du">Convention valable à partir du</label><input id="cv-du" type="date" value={du} onChange={(e) => setDu(e.target.value)} style={inputStyle} /></div>
              <div style={{ flex: '1 1 150px' }}><label style={lbl} htmlFor="cv-au">… jusqu’au</label><input id="cv-au" type="date" value={au} onChange={(e) => setAu(e.target.value)} style={inputStyle} /></div>
            </div>
            <div>
              <label style={lbl} htmlFor="cv-exc">Dates sautées (clinique fermée…)</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <input id="cv-exc" type="date" value={nouvelleExc} onChange={(e) => setNouvelleExc(e.target.value)} style={{ ...inputStyle, width: 170 }} />
                <Bouton taille="sm" type="button" disabled={!nouvelleExc} onClick={() => { setExceptions([...new Set([...exceptions, nouvelleExc])].sort()); setNouvelleExc(''); }}>Ajouter</Bouton>
                {exceptions.map((e) => (
                  <Bouton taille="sm" key={e} type="button" title="Retirer cette date" onClick={() => setExceptions(exceptions.filter((x) => x !== e))}>
                    {e.slice(8)}/{e.slice(5, 7)}/{e.slice(0, 4)} ✕
                  </Bouton>
                ))}
              </div>
            </div>
            <div><label style={lbl} htmlFor="cv-note">Note</label>
              <textarea id="cv-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} style={{ ...inputStyle, height: 'auto', padding: '8px 10px', resize: 'vertical' }} /></div>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
              <input type="checkbox" checked={actif} onChange={(e) => setActif(e.target.checked)} />{estEvt ? 'Événement actif' : 'Convention active'} (décoché, il disparaît du calendrier sans être supprimé)
            </label>
          </div>
        </details>
      </div>
    </Modale>
  );
}
